import React, { useState, useEffect } from 'react';
import { Card } from './card';
import { Input } from './input';
import { Button } from './button';
import { Avatar, AvatarFallback, AvatarImage } from './avatar';
import { Badge } from './badge';
import { Checkbox } from './checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Search, Filter, Users, Grid3X3, List } from 'lucide-react';

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  address?: string;
  profileImageUrl?: string;
  primarySkills?: string[];
  secondarySkills?: string[];
  role: 'aspirant' | 'mentor';
}

interface UserSelectionGridProps {
  users: User[];
  selectedUsers: User[];
  onUserToggle: (user: User) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  loading?: boolean;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  onPageChange?: (page: number) => void;
  onSearchChange?: (search: string) => void;
  onFilterChange?: (filters: { role: string; hasProfilePic: string }) => void;
}

export const UserSelectionGrid: React.FC<UserSelectionGridProps> = ({
  users,
  selectedUsers,
  onUserToggle,
  onSelectAll,
  onClearSelection,
  loading = false,
  pagination,
  onPageChange,
  onSearchChange,
  onFilterChange
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [hasProfilePicFilter, setHasProfilePicFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const isUserSelected = (user: User) => {
    return selectedUsers.some(selected => selected._id === user._id);
  };

  const getSelectedCount = () => {
    // Count selected users from the current page only
    return filteredUsers.filter(user => isUserSelected(user)).length;
  };

  const handleSelectAll = () => {
    const currentPageSelectedCount = getSelectedCount();
    if (currentPageSelectedCount === filteredUsers.length && filteredUsers.length > 0) {
      // Deselect all users from current page
      filteredUsers.forEach(user => {
        if (isUserSelected(user)) {
          onUserToggle(user);
        }
      });
    } else {
      // Select all users from current page
      filteredUsers.forEach(user => {
        if (!isUserSelected(user)) {
          onUserToggle(user);
        }
      });
    }
  };

  // Handle search change
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    onSearchChange?.(value);
  };

  // Handle filter changes
  const handleRoleFilterChange = (value: string) => {
    setRoleFilter(value);
    onFilterChange?.({ role: value, hasProfilePic: hasProfilePicFilter });
  };

  const handleProfilePicFilterChange = (value: string) => {
    setHasProfilePicFilter(value);
    onFilterChange?.({ role: roleFilter, hasProfilePic: value });
  };

  // Use all users since filtering is now handled by the backend
  const filteredUsers = users;

  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search by name, email, phone, city, skills..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="flex gap-2">
          <Select value={roleFilter} onValueChange={handleRoleFilterChange}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="aspirant">Aspirants</SelectItem>
              <SelectItem value="mentor">Mentors</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={hasProfilePicFilter} onValueChange={handleProfilePicFilterChange}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Profile Pic" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Users</SelectItem>
              <SelectItem value="true">With Profile Pic</SelectItem>
              <SelectItem value="false">Without Profile Pic</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Selection Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSelectAll}
            disabled={filteredUsers.length === 0}
          >
            {getSelectedCount() === filteredUsers.length && filteredUsers.length > 0 
              ? 'Deselect All' 
              : 'Select All'
            }
          </Button>
          
          {selectedUsers.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={onClearSelection}
            >
              Clear Selection
            </Button>
          )}
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-600">
            {getSelectedCount()} of {filteredUsers.length} users selected
          </div>
          
          {/* View Toggle */}
          <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="h-8 px-3"
            >
              <Grid3X3 className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="h-8 px-3"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Users Display */}
      {loading ? (
        <div className={viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" : "space-y-3"}>
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="p-4 animate-pulse">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>No users found matching your criteria</p>
        </div>
      ) : (
        <div className={viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" : "space-y-3"}>
          {filteredUsers.map((user) => (
            <Card 
              key={user._id} 
              className={`p-4 cursor-pointer transition-all duration-200 hover:shadow-md ${
                isUserSelected(user) ? 'ring-2 ring-primary bg-primary/5' : ''
              }`}
              onClick={() => onUserToggle(user)}
            >
              {viewMode === 'grid' ? (
                // Grid View Layout
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <Checkbox 
                      checked={isUserSelected(user)}
                      onChange={() => onUserToggle(user)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3">
                      <Avatar className="w-10 h-10">
                        <AvatarImage 
                          src={user.profileImageUrl} 
                          alt={`${user.firstName} ${user.lastName}`}
                        />
                        <AvatarFallback className="bg-primary text-white text-sm">
                          {getInitials(user.firstName, user.lastName)}
                        </AvatarFallback>
                      </Avatar>
                      
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 truncate">
                          {user.firstName} {user.lastName}
                        </h4>
                        <p className="text-sm text-gray-500 truncate">{user.email}</p>
                      </div>
                    </div>
                    
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge variant={user.role === 'mentor' ? 'default' : 'secondary'}>
                          {user.role}
                        </Badge>
                        {user.profileImageUrl && (
                          <Badge variant="outline" className="text-xs">
                            Has Photo
                          </Badge>
                        )}
                      </div>
                      
                      {user.primarySkills && user.primarySkills.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {user.primarySkills.slice(0, 2).map((skill, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {skill}
                            </Badge>
                          ))}
                          {user.primarySkills.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{user.primarySkills.length - 2}
                            </Badge>
                          )}
                        </div>
                      )}
                      
                      {user.address && (
                        <p className="text-xs text-gray-500 truncate">
                          📍 {user.address.split(',').slice(-1)[0]?.trim()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                // List View Layout
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <Checkbox 
                      checked={isUserSelected(user)}
                      onChange={() => onUserToggle(user)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  
                  <Avatar className="w-12 h-12 flex-shrink-0">
                    <AvatarImage 
                      src={user.profileImageUrl} 
                      alt={`${user.firstName} ${user.lastName}`}
                    />
                    <AvatarFallback className="bg-primary text-white text-lg font-semibold">
                      {getInitials(user.firstName, user.lastName)}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-gray-900 truncate">
                        {user.firstName} {user.lastName}
                      </h4>
                      <div className="flex items-center gap-2">
                        <Badge variant={user.role === 'mentor' ? 'default' : 'secondary'}>
                          {user.role}
                        </Badge>
                        {user.profileImageUrl && (
                          <Badge variant="outline" className="text-xs">
                            Has Photo
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-600 truncate mt-1">{user.email}</p>
                    
                    <div className="flex items-center space-x-4 mt-2">
                      {user.primarySkills && user.primarySkills.length > 0 && (
                        <span className="text-xs text-gray-500">
                          {user.primarySkills.slice(0, 3).join(', ')}
                          {user.primarySkills.length > 3 && '...'}
                        </span>
                      )}
                      
                      {user.address && (
                        <span className="text-xs text-gray-500">
                          📍 {user.address.split(',').slice(-1)[0]?.trim()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange?.(pagination.page - 1)}
            disabled={pagination.page <= 1}
          >
            Previous
          </Button>
          
          <span className="text-sm text-gray-600">
            Page {pagination.page} of {pagination.pages}
          </span>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange?.(pagination.page + 1)}
            disabled={pagination.page >= pagination.pages}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};
